// Messaging Reports (/analytics/messaging). Modeled on the Calls page
// (RhinoVOIPReports): same filter bar, tabs, Graphs/Table toggle, CSV export
// and palette. One API call (GET /analytics/messages/breakdown) returns counts
// grouped finely enough that every filter and chart is derived in the browser.
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import {
  Button, DateRangeDropdown, Dropdown, DropdownMenuItem, Icon, UtilityInlineGrid,
} from 'rhinostyle';
import PageLoader from './PageLoader';
import AskReggieButton from './AskReggieButton';
import {
  ChartCard, DonutChart, HorizontalBars, StackedBars, MultiLine, SortableTable, GroupingSelector, EmptyChart, ResponseByHourChart, csvColumns,
  MAX_SERIES, OTHER_LABEL, OTHER_COLOR,
} from './MessagingReportsCharts';
import {
  useGetMessageBreakdownQuery, useGetInboundContactsQuery, useGetMessagesByGroupQuery, useGetRemindersSummaryQuery,
} from '../services/AnalyticsService';
import {
  REPORT_DATE_PICKER_LABELS, REPORT_TIMEZONE_OPTIONS, REPORT_DIRECTION_COLORS, REPORT_PIE_COLORS,
  categoryColor, readStoredTimezone, storeTimezone,
} from '../helpers/ReportsHelpers';
import {
  MESSAGE_KINDS, channelTypeLabel, filterRows, sumRows, daysInRange, summaryByPeriod, kindPie,
  perDaySeries, perHourSeries, perWeekdaySeries, peakInboundHour, totalsByCategory, categoryByPeriod, dailyByCategory,
  formatMinutes, OTHER_KEY,
} from '../helpers/MessagingReportsHelpers';
import { clearAskReggieContext, setAskReggieContext } from '../reducers/rhinoanalystReducer';
import { capitalize } from '../helpers/StringHelpers';

const TZ_STORAGE_KEY = 'messagingReportsTimezone';
const MAX_RANGE_MONTHS = 12;

const REPORT_TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'members', label: 'Members' },
  { id: 'channels', label: 'Channels' },
  { id: 'groups', label: 'Groups' },
  { id: 'reminders', label: 'Appointment Reminders' },
];

const DIRECTION_BARS = [
  { key: 'inbound', name: 'Inbound', color: REPORT_DIRECTION_COLORS.Inbound },
  { key: 'outbound', name: 'Outbound', color: REPORT_DIRECTION_COLORS.Outbound },
  { key: 'automated', name: 'Automated', color: REPORT_DIRECTION_COLORS.Automated },
];

const REMINDER_COLORS = {
  Confirmed: '#5DBE85',
  Cancelled: '#E94B4F',
  'No Response': '#B5B7BD',
};

// Series keys are ids as strings, or OTHER_KEY for the folded remainder.
const keyId = (k) => (k === OTHER_KEY ? OTHER_KEY : Number(k));
const seriesName = (k, nameOf) => (k === OTHER_KEY ? OTHER_LABEL : nameOf(Number(k)));
const kindColor = (name) => REPORT_DIRECTION_COLORS[name] || REPORT_PIE_COLORS[0];
const indexColor = (name, index) => categoryColor(index);
const pieOf = (rows, nameKey = 'name', valueKey = 'total') => rows.filter((r) => r[valueKey] > 0).map((r) => ({ name: r[nameKey], value: r[valueKey] }));

const MessagingReports = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('summary');
  const [viewMode, setViewMode] = useState('graphs');
  const [startDate, setStartDate] = useState(moment().subtract(29, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [activeKey, setActiveKey] = useState(3);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedKind, setSelectedKind] = useState('all');
  const [tzPref, setTzPref] = useState(() => readStoredTimezone(TZ_STORAGE_KEY));
  const [summaryGrouping, setSummaryGrouping] = useState('total');
  const [memberGrouping, setMemberGrouping] = useState('total');
  const [channelGrouping, setChannelGrouping] = useState('total');

  useEffect(() => { storeTimezone(TZ_STORAGE_KEY, tzPref); }, [tzPref]);

  // 'auto' resolves to the browser zone each render so travel is reflected.
  const tz = useMemo(() => (tzPref === 'auto' ? moment.tz.guess() : tzPref), [tzPref]);
  const tzLabel = useMemo(() => {
    const opt = REPORT_TIMEZONE_OPTIONS.find((o) => o.id === tzPref);
    if (!opt) return 'My Current Timezone';
    return tzPref === 'auto' ? `My Current Timezone (${tz})` : opt.label;
  }, [tzPref, tz]);

  // The picker returns browser-local moments; reinterpret the calendar dates
  // in the chosen zone so "Apr 1" means Apr 1 there. The API buckets in `tz`.
  const pickedStart = useMemo(() => moment.tz(startDate.format('YYYY-MM-DD'), tz).startOf('day'), [startDate, tz]);
  const pickedEnd = useMemo(() => moment.tz(endDate.format('YYYY-MM-DD'), tz).startOf('day'), [endDate, tz]);
  const from = pickedStart.format('YYYY-MM-DD');
  const to = pickedEnd.format('YYYY-MM-DD');
  const days = useMemo(() => daysInRange(pickedStart, pickedEnd), [pickedStart, pickedEnd]);

  const { data: breakdown, isLoading, isFetching } = useGetMessageBreakdownQuery({ from, to, tz });
  const { data: contacts } = useGetInboundContactsQuery({ from, to });
  const { data: groupsData, isFetching: groupsFetching } = useGetMessagesByGroupQuery({ from, to }, { skip: activeTab !== 'groups' });
  const { data: remindersData, isFetching: remindersFetching } = useGetRemindersSummaryQuery({ from, to }, { skip: activeTab !== 'reminders' });

  const members = breakdown?.members || [];
  const channels = breakdown?.channels || [];
  const memberName = useCallback((id) => {
    const m = members.find((member) => member.id === id);
    if (!m) return `Member ${id}`;
    return m.deleted ? `${m.name} (deleted)` : m.name;
  }, [members]);
  const channelName = useCallback((id) => {
    const c = channels.find((ch) => ch.id === id);
    if (!c) return `Channel ${id}`;
    return c.deleted ? `${c.name} (deleted)` : c.name;
  }, [channels]);

  const filters = useMemo(() => ({ memberId: selectedMember, channelId: selectedChannel, kind: selectedKind }), [selectedMember, selectedChannel, selectedKind]);
  const daily = useMemo(() => filterRows(breakdown?.daily || [], filters), [breakdown, filters]);
  const byHour = useMemo(() => filterRows(breakdown?.byHour || [], filters), [breakdown, filters]);
  const byWeekday = useMemo(() => filterRows(breakdown?.byWeekday || [], filters), [breakdown, filters]);
  const hasData = daily.length > 0;

  // --- Summary ---
  const totals = useMemo(() => sumRows(daily), [daily]);
  const summaryRows = useMemo(() => summaryByPeriod(daily, days, summaryGrouping), [daily, days, summaryGrouping]);
  const directionPie = useMemo(() => kindPie(totals), [totals]);
  const channelPie = useMemo(() => pieOf(totalsByCategory(daily, (r) => r.channelId, channelName, days.length)), [daily, channelName, days.length]);
  const channelTypePie = useMemo(() => {
    const typeOf = (id) => channelTypeLabel(channels.find((c) => c.id === id)?.typeId);
    return pieOf(totalsByCategory(daily, (r) => typeOf(r.channelId), (k) => k, days.length));
  }, [daily, channels, days.length]);
  const hourSeries = useMemo(() => perHourSeries(byHour, days.length), [byHour, days.length]);
  const weekdaySeries = useMemo(() => perWeekdaySeries(byWeekday, days), [byWeekday, days]);
  const daySeries = useMemo(() => perDaySeries(daily, days), [daily, days]);
  const peak = useMemo(() => peakInboundHour(byHour), [byHour]);
  const avgResponse = useMemo(() => {
    const withResponse = daySeries.filter((d) => d.avgResponseMinutes !== null);
    return { points: daySeries, avg: summaryByPeriod(daily, days, 'total')[0]?.avgResponseMinutes ?? null, hasAny: withResponse.length > 0 };
  }, [daySeries, daily, days]);
  const contactPie = useMemo(() => {
    if (!contacts?.contacts) return [];
    return Object.entries(contacts.contacts).map(([source, value]) => ({ name: capitalize(source), value })).filter((s) => s.value > 0);
  }, [contacts]);

  // --- Members (outbound only: inbound senders are contacts) ---
  const memberRows = useMemo(() => filterRows(daily, { kind: 'outbound' }), [daily]);
  const memberTotals = useMemo(() => totalsByCategory(memberRows, (r) => r.memberId, memberName, days.length), [memberRows, memberName, days.length]);
  const memberTable = useMemo(() => categoryByPeriod(memberRows, (r) => r.memberId, memberName, days, memberGrouping), [memberRows, memberName, days, memberGrouping]);
  const memberDaily = useMemo(() => dailyByCategory(memberRows, (r) => r.memberId, days), [memberRows, days]);
  const memberResponse = useMemo(() => memberTotals.filter((m) => m.avgResponseMinutes !== null), [memberTotals]);

  // --- Channels ---
  const channelTotals = useMemo(() => totalsByCategory(daily, (r) => r.channelId, channelName, days.length), [daily, channelName, days.length]);
  const channelTable = useMemo(() => categoryByPeriod(daily, (r) => r.channelId, channelName, days, channelGrouping), [daily, channelName, days, channelGrouping]);
  const channelDaily = useMemo(() => dailyByCategory(daily, (r) => r.channelId, days), [daily, days]);
  const channelResponse = useMemo(() => channelTotals.filter((c) => c.avgResponseMinutes !== null), [channelTotals]);

  // --- Groups / Reminders (date range only; existing endpoints) ---
  // Groups: { groups: [{groupId, name, routed, open, closed}], closedByDay: [{day, groupId, count}] }
  // (an older API shape was a plain array of { groupId, name, conversationCount }).
  const groupData = useMemo(() => {
    const raw = Array.isArray(groupsData) ? { groups: groupsData, closedByDay: [] } : (groupsData || {});
    const rows = (raw.groups || []).map((g) => ({
      key: g.groupId,
      name: `${g.name || `Group ${g.groupId}`}${g.deleted ? ' (deleted)' : ''}`,
      routed: Number(g.routed ?? g.conversationCount) || 0,
      open: Number(g.open) || 0,
      closed: Number(g.closed) || 0,
    })).sort((a, b) => b.routed - a.routed || a.name.localeCompare(b.name));
    const nameOf = (id) => (id === OTHER_KEY ? OTHER_LABEL : (rows.find((r) => r.key === id)?.name || `Group ${id}`));
    const closedTotals = {};
    (raw.closedByDay || []).forEach((r) => { closedTotals[r.groupId] = (closedTotals[r.groupId] || 0) + (Number(r.count) || 0); });
    const ranked = Object.keys(closedTotals).sort((a, b) => closedTotals[b] - closedTotals[a]);
    const keys = ranked.length > MAX_SERIES ? [...ranked.slice(0, MAX_SERIES - 1), OTHER_KEY] : ranked;
    const own = new Set(keys);
    const byDay = {};
    days.forEach((day) => {
      byDay[day] = { date: moment(day, 'YYYY-MM-DD').format('MM/DD'), day };
      keys.forEach((k) => { byDay[day][k] = 0; });
    });
    (raw.closedByDay || []).forEach((r) => {
      const key = String(r.groupId);
      if (byDay[r.day]) byDay[r.day][own.has(key) ? key : OTHER_KEY] += Number(r.count) || 0;
    });
    return {
      rows,
      nameOf,
      closedPerDay: days.map((day) => byDay[day]),
      closedKeys: keys,
      totalOpen: rows.reduce((sum, r) => sum + r.open, 0),
      totalClosed: rows.reduce((sum, r) => sum + r.closed, 0),
    };
  }, [groupsData, days]);
  const reminders = useMemo(() => {
    const total = Number(remindersData?.total) || 0;
    const byResponse = (remindersData?.byResponse || []).map((r) => ({ name: r.label, value: Number(r.count) || 0 })).filter((r) => r.value > 0);
    const dayMap = {};
    days.forEach((day) => { dayMap[day] = { date: moment(day, 'YYYY-MM-DD').format('MM/DD') }; });
    (remindersData?.byDay || []).forEach((d) => {
      const key = String(d.day).slice(0, 10);
      if (!dayMap[key]) return;
      dayMap[key][d.label] = (dayMap[key][d.label] || 0) + (Number(d.count) || 0);
    });
    const labels = Object.keys(REMINDER_COLORS).filter((label) => byResponse.some((r) => r.name === label));
    const perDay = days.map((day) => ({ ...dayMap[day], ...Object.fromEntries(labels.map((l) => [l, dayMap[day][l] || 0])) }));
    const count = (label) => byResponse.find((r) => r.name === label)?.value || 0;
    const rate = (n) => (total ? Math.round((n / total) * 100) : 0);
    const rows = labels.map((label) => ({ response: label, count: count(label), pct: rate(count(label)) }));
    return { total, byResponse, perDay, labels, rows, responded: total - count('No Response'), rate };
  }, [remindersData, days]);

  // --- Filters / dates ---
  const memberLabel = selectedMember === null ? 'All Members' : memberName(selectedMember);
  const channelLabel = selectedChannel === null ? 'All Channels' : channelName(selectedChannel);
  const kindLabel = MESSAGE_KINDS.find((k) => k.id === selectedKind)?.label || 'All Messages';
  const isAnyFilterSelected = selectedMember !== null || selectedChannel !== null || selectedKind !== 'all';
  const clearAllFilters = () => { setSelectedMember(null); setSelectedChannel(null); setSelectedKind('all'); };

  const selectDate = (date) => {
    if (date && date.startDate && date.endDate) {
      const start = moment(date.startDate);
      const end = moment(date.endDate);
      if (end.diff(start, 'months', true) > MAX_RANGE_MONTHS) {
        start.set(end.clone().subtract(MAX_RANGE_MONTHS, 'months').toObject());
      }
      setStartDate(start);
      setEndDate(end);
      setActiveKey(date.activeKey);
    }
  };

  const calculateDateRange = (duration, selectedLabelIndex) => {
    let fromDate = moment().subtract(duration, 'days');
    let toDate = moment();
    if (selectedLabelIndex === 1) toDate = moment().subtract(1, 'days');
    if (duration === 'year') fromDate = moment().subtract(1, 'years').add(1, 'days');
    return { startDate: fromDate, endDate: toDate };
  };

  const csvFilename = (title) => `${title}_${from}-${to}.csv`;

  // Publish what is on screen to the Ask Reggie drawer so its answers follow
  // the date range and filters chosen here (same as the Calls page).
  useEffect(() => {
    const rangeText = days.length === 30 ? 'Last 30 days' : `${pickedStart.format('MMM D')} - ${pickedEnd.format('MMM D, YYYY')}`;
    const contextFilters = { tab: activeTab, timezone: tz };
    if (selectedMember !== null) contextFilters.member = memberLabel;
    if (selectedChannel !== null) contextFilters.channel = channelLabel;
    if (selectedKind !== 'all') contextFilters['message type'] = kindLabel;
    dispatch(setAskReggieContext({
      page: 'Messaging Reports',
      label: `Messaging • ${rangeText}`,
      filters: contextFilters,
      dateRange: { from, to },
    }));
  }, [dispatch, days.length, pickedStart, pickedEnd, activeTab, tz, selectedMember, memberLabel, selectedChannel, channelLabel, selectedKind, kindLabel, from, to]);

  useEffect(() => () => { dispatch(clearAskReggieContext()); }, [dispatch]);

  const chart = (height, node) => (hasData ? node : <EmptyChart height={height} />);
  const categoryColumns = (label, grouping) => [
    { key: 'name', label },
    ...(grouping !== 'total' ? [{ key: 'period', label: 'Period' }] : []),
    { key: 'total', label: 'Total Messages' },
    { key: 'avgPerDay', label: 'Avg. Messages/Day' },
    { key: 'inbound', label: '# Inbound' },
    { key: 'outbound', label: '# Outbound' },
    { key: 'automated', label: '# Automated' },
    { key: 'avgResponseMinutes', label: 'Avg. Response Time', format: (r) => formatMinutes(r.avgResponseMinutes) },
  ];
  const summaryColumns = [
    { key: 'period', label: 'Period' },
    { key: 'total', label: 'Total Messages' },
    { key: 'avgPerDay', label: 'Avg. Messages/Day' },
    { key: 'inbound', label: '# Inbound' },
    { key: 'outbound', label: '# Outbound' },
    { key: 'automated', label: '# Automated' },
    { key: 'avgResponseMinutes', label: 'Avg. Response Time', format: (r) => formatMinutes(r.avgResponseMinutes) },
  ];

  const renderFilters = () => (
    <div className="cdr-records__filter-wrapper">
      <div className="cdr-records__filter-label">
        FILTER BY:
        {isAnyFilterSelected && (
          <Button type="link" className="clear-all__button" onClick={clearAllFilters}>Clear All &nbsp;<Icon icon="close" /></Button>
        )}
      </div>
      <div className="row">
        <div>
          <UtilityInlineGrid>
            <DateRangeDropdown
              name="Messaging Reports Date Picker"
              startDate={startDate}
              endDate={endDate}
              selectDate={selectDate}
              minDate={moment().subtract(1, 'year')}
              maxDate={moment()}
              activeKey={activeKey}
              position="center"
              dropdownMenuItems={REPORT_DATE_PICKER_LABELS}
              selectDateRange={calculateDateRange}
              dataCypress="messagingReportsDatePicker"
              isCustomDate
            />
            <Dropdown
              position="bottom"
              activeKey={selectedMember === null ? '' : selectedMember}
              label={memberLabel}
              type={selectedMember !== null ? 'primary' : 'input'}
              outlined={selectedMember !== null}
              name="messagingReportsMember"
              onSelect={(id) => setSelectedMember(id === 'all' || id === selectedMember ? null : id)}
              dataCypress="messagingReportsMember"
            >
              <DropdownMenuItem id="all" label="All Members" icon={selectedMember === null ? 'checkmark' : ''} />
              {members.map((m) => (
                <DropdownMenuItem key={m.id} id={m.id} label={m.name} icon={selectedMember === m.id ? 'checkmark' : ''} />
              ))}
            </Dropdown>
            <Dropdown
              position="bottom"
              activeKey={selectedChannel === null ? '' : selectedChannel}
              label={channelLabel}
              type={selectedChannel !== null ? 'primary' : 'input'}
              outlined={selectedChannel !== null}
              name="messagingReportsChannel"
              onSelect={(id) => setSelectedChannel(id === 'all' || id === selectedChannel ? null : id)}
              dataCypress="messagingReportsChannel"
            >
              <DropdownMenuItem id="all" label="All Channels" icon={selectedChannel === null ? 'checkmark' : ''} />
              {channels.map((c) => (
                <DropdownMenuItem key={c.id} id={c.id} label={`${channelName(c.id)} (${channelTypeLabel(c.typeId)})`} icon={selectedChannel === c.id ? 'checkmark' : ''} />
              ))}
            </Dropdown>
            <Dropdown
              position="bottom"
              activeKey={selectedKind}
              label={kindLabel}
              type={selectedKind !== 'all' ? 'primary' : 'input'}
              outlined={selectedKind !== 'all'}
              name="messagingReportsKind"
              onSelect={(id) => setSelectedKind(id)}
              dataCypress="messagingReportsKind"
            >
              {MESSAGE_KINDS.map((k) => (
                <DropdownMenuItem key={k.id} id={k.id} label={k.label} icon={selectedKind === k.id ? 'checkmark' : ''} />
              ))}
            </Dropdown>
            <Dropdown
              position="bottom"
              activeKey={tzPref}
              label={tzLabel}
              type={tzPref !== 'auto' ? 'primary' : 'input'}
              outlined={tzPref !== 'auto'}
              name="messagingReportsTimezone"
              onSelect={(id) => setTzPref(id)}
              dataCypress="messagingReportsTimezone"
            >
              {REPORT_TIMEZONE_OPTIONS.map((opt) => (
                <DropdownMenuItem key={opt.id} id={opt.id} label={opt.label} icon={tzPref === opt.id ? 'checkmark' : ''} />
              ))}
            </Dropdown>
          </UtilityInlineGrid>
        </div>
      </div>
    </div>
  );

  const renderTabs = () => (
    <div className="voip-reports__tabs">
      <div className="voip-reports__tabs-left">
        {REPORT_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`voip-reports__tab${activeTab === tab.id ? ' voip-reports__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="voip-reports__view-toggle">
        {['graphs', 'table'].map((mode) => (
          <button
            key={mode}
            type="button"
            className={`voip-reports__view-btn${viewMode === mode ? ' voip-reports__view-btn--active' : ''}`}
            onClick={() => setViewMode(mode)}
          >
            {mode === 'graphs' ? 'Graphs' : 'Table'}
          </button>
        ))}
      </div>
    </div>
  );

  const renderSummary = () => (
    <div className="voip-reports__charts">
      {viewMode === 'table' && (
        <ChartCard
          title="Message Summary"
          csv={{ columns: csvColumns(summaryColumns), rows: summaryRows, filename: csvFilename('Message Summary') }}
          actions={<GroupingSelector value={summaryGrouping} onChange={setSummaryGrouping} />}
        >
          <SortableTable columns={summaryColumns} rows={summaryRows} defaultSort="period" defaultAsc rowKey={(r) => r.period} />
        </ChartCard>
      )}
      {viewMode === 'graphs' && (
        <>
          <div className="voip-reports__pie-row">
            <ChartCard half title={`Messages by Direction (${totals.total} total, ${summaryRows.length === 1 ? summaryRows[0].avgPerDay : Math.round(totals.total / Math.max(days.length, 1))}/day)`} csv={{ columns: [{ key: 'name', header: 'Direction' }, { key: 'value', header: 'Messages' }], rows: directionPie, filename: csvFilename('Messages by Direction') }}>
              <DonutChart data={directionPie} colorFor={kindColor} />
            </ChartCard>
            <ChartCard half title="Messages by Channel" csv={{ columns: [{ key: 'name', header: 'Channel' }, { key: 'value', header: 'Messages' }], rows: channelPie, filename: csvFilename('Messages by Channel') }}>
              <DonutChart data={channelPie} colorFor={indexColor} />
            </ChartCard>
          </div>
          <div className="voip-reports__pie-row">
            <ChartCard half title="Messages by Channel Type" csv={{ columns: [{ key: 'name', header: 'Channel Type' }, { key: 'value', header: 'Messages' }], rows: channelTypePie, filename: csvFilename('Messages by Channel Type') }}>
              <DonutChart data={channelTypePie} colorFor={indexColor} />
            </ChartCard>
            <ChartCard half title={`New Inbound Contacts by Source${contacts?.total ? ` (${contacts.total} total${contacts.responseRate !== null && contacts.responseRate !== undefined ? `, ${Math.round(contacts.responseRate)}% responded to` : ''})` : ''}`} csv={{ columns: [{ key: 'name', header: 'Source' }, { key: 'value', header: 'Contacts' }], rows: contactPie, filename: csvFilename('New Inbound Contacts by Source') }}>
              <DonutChart data={contactPie} colorFor={indexColor} valueLabel="Contacts" />
            </ChartCard>
          </div>
          <ChartCard title={`Average Messages Per Hour${peak ? ` (peak inbound ${peak.label})` : ''}`} csv={{ columns: [{ key: 'hour', header: 'Hour' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }, { key: 'automated', header: 'Automated' }], rows: hourSeries, filename: csvFilename('Average Messages Per Hour') }}>
            {chart(300, <StackedBars data={hourSeries} xKey="hour" bars={DIRECTION_BARS} stackId="hr" interval={1} />)}
          </ChartCard>
          <ChartCard title="Average Response Time by Hour" csv={{ columns: [{ key: 'hour', header: 'Hour' }, { key: 'avgResponseMinutes', header: 'Avg. Response (min)' }, { key: 'replies', header: 'Replies' }], rows: hourSeries, filename: csvFilename('Average Response Time by Hour') }}>
            {hourSeries.some((h) => h.avgResponseMinutes !== null) ? (
              <ResponseByHourChart data={hourSeries} />
            ) : <EmptyChart height={300} />}
          </ChartCard>
          <ChartCard title="Average Messages by Day of Week" csv={{ columns: [{ key: 'day', header: 'Day' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }, { key: 'automated', header: 'Automated' }], rows: weekdaySeries, filename: csvFilename('Average Messages by Day of Week') }}>
            {chart(300, <StackedBars data={weekdaySeries} xKey="day" bars={DIRECTION_BARS} stackId="dow" interval={0} />)}
          </ChartCard>
          <ChartCard title="Inbound vs Outbound Messages Per Day" csv={{ columns: [{ key: 'day', header: 'Date' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }, { key: 'automated', header: 'Automated' }], rows: daySeries, filename: csvFilename('Messages Per Day') }}>
            {chart(300, <StackedBars data={daySeries} xKey="date" bars={DIRECTION_BARS} stackId="day" />)}
          </ChartCard>
          <ChartCard title={`Response Time Per Day${avgResponse.avg !== null ? ` (avg. ${avgResponse.avg} min)` : ''}`} csv={{ columns: [{ key: 'day', header: 'Date' }, { key: 'avgResponseMinutes', header: 'Avg. Response (min)' }], rows: daySeries, filename: csvFilename('Response Time Per Day') }}>
            {avgResponse.hasAny ? (
              <MultiLine
                data={daySeries.map((d) => ({ ...d, average: avgResponse.avg }))}
                series={[{ key: 'avgResponseMinutes', name: 'Avg. Response (min)', color: REPORT_DIRECTION_COLORS.Inbound }, { key: 'average', name: 'Range Average (min)', color: '#B5B7BD' }]}
                height={300}
                formatter={(v) => formatMinutes(v)}
              />
            ) : <EmptyChart height={300} />}
          </ChartCard>
        </>
      )}
    </div>
  );

  const renderCategoryTab = ({ label, totalsRows, tableRows, dailySeries, responseRows, grouping, setGrouping, nameOf }) => {
    const columns = categoryColumns(label, grouping);
    return (
      <div className="voip-reports__charts">
        {viewMode === 'table' && (
          <ChartCard
            title={`${label} Breakdown`}
            csv={{ columns: csvColumns(columns), rows: tableRows, filename: csvFilename(`${label} Breakdown`) }}
            actions={<GroupingSelector value={grouping} onChange={setGrouping} />}
          >
            <SortableTable columns={columns} rows={tableRows} defaultSort={grouping === 'total' ? 'total' : 'name'} defaultAsc={grouping !== 'total'} rowKey={(r) => `${r.key}-${r.period || ''}`} />
          </ChartCard>
        )}
        {viewMode === 'graphs' && (
          <>
            <div className="voip-reports__pie-row">
              <ChartCard half title={`Message Distribution Per ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'total', header: 'Messages' }], rows: totalsRows, filename: csvFilename(`Message Distribution Per ${label}`) }}>
                <DonutChart data={pieOf(totalsRows)} colorFor={indexColor} />
              </ChartCard>
              <ChartCard half title={`Average Response Time by ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'avgResponseMinutes', header: 'Avg. Response (min)' }], rows: responseRows, filename: csvFilename(`Average Response Time by ${label}`) }}>
                <HorizontalBars data={responseRows} bars={[{ key: 'avgResponseMinutes', name: 'Avg. Response (min)', color: REPORT_DIRECTION_COLORS.Inbound }]} formatter={(v) => formatMinutes(v)} />
              </ChartCard>
            </div>
            <ChartCard title={`Total Messages Per ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'total', header: 'Messages' }], rows: totalsRows, filename: csvFilename(`Total Messages Per ${label}`) }}>
              <HorizontalBars data={totalsRows} bars={[{ key: 'total', name: 'Messages' }]} />
            </ChartCard>
            <ChartCard
              title={`Daily Messages Per ${label}`}
              csv={{ columns: [{ key: 'day', header: 'Date' }, ...dailySeries.keys.map((k) => ({ key: k, header: seriesName(k, nameOf) }))], rows: dailySeries.data, filename: csvFilename(`Daily Messages Per ${label}`) }}
            >
              <MultiLine
                data={dailySeries.data}
                series={dailySeries.keys.map((k) => ({ key: k, name: seriesName(k, nameOf), color: k === OTHER_KEY ? OTHER_COLOR : undefined }))}
              />
            </ChartCard>
            <ChartCard title={`Average Messages Per Day by ${label}`} csv={{ columns: [{ key: 'name', header: label }, { key: 'avgPerDay', header: 'Avg. Messages/Day' }], rows: totalsRows, filename: csvFilename(`Average Messages Per Day by ${label}`) }}>
              <HorizontalBars data={totalsRows} bars={[{ key: 'avgPerDay', name: 'Avg. Messages/Day' }]} />
            </ChartCard>
            {label === 'Channel' && (
              <ChartCard title="Inbound vs Outbound Per Channel" csv={{ columns: [{ key: 'name', header: 'Channel' }, { key: 'inbound', header: 'Inbound' }, { key: 'outbound', header: 'Outbound' }, { key: 'automated', header: 'Automated' }], rows: totalsRows, filename: csvFilename('Inbound vs Outbound Per Channel') }}>
                <HorizontalBars data={totalsRows} bars={DIRECTION_BARS} />
              </ChartCard>
            )}
          </>
        )}
      </div>
    );
  };

  const renderGroups = () => {
    const { rows, nameOf, closedPerDay, closedKeys, totalOpen, totalClosed } = groupData;
    const columns = [
      { key: 'name', label: 'Group' },
      { key: 'routed', label: 'Conversations Routed' },
      { key: 'closed', label: 'Conversations Closed' },
      { key: 'open', label: 'Open Now' },
    ];
    if (groupsFetching) return <PageLoader />;
    return (
      <div className="voip-reports__charts">
        {viewMode === 'table' && (
          <ChartCard title="Group Breakdown" csv={{ columns: csvColumns(columns), rows, filename: csvFilename('Group Breakdown') }}>
            <SortableTable columns={columns} rows={rows} defaultSort="routed" rowKey={(r) => r.key} />
          </ChartCard>
        )}
        {viewMode === 'graphs' && (
          <>
            <ChartCard title="Conversations Routed Per Group" csv={{ columns: [{ key: 'name', header: 'Group' }, { key: 'routed', header: 'Conversations Routed' }], rows, filename: csvFilename('Conversations Routed Per Group') }}>
              <HorizontalBars data={rows.filter((r) => r.routed > 0)} bars={[{ key: 'routed', name: 'Conversations Routed' }]} />
            </ChartCard>
            <ChartCard title={`Closed Conversations Per Day by Group (${totalClosed} total)`} csv={{ columns: [{ key: 'day', header: 'Date' }, ...closedKeys.map((k) => ({ key: k, header: nameOf(keyId(k)) }))], rows: closedPerDay, filename: csvFilename('Closed Conversations Per Day by Group') }}>
              <StackedBars data={closedPerDay} xKey="date" bars={closedKeys.map((k, i) => ({ key: k, name: nameOf(keyId(k)), color: k === OTHER_KEY ? OTHER_COLOR : categoryColor(i) }))} stackId="closed" />
            </ChartCard>
            <ChartCard title={`Open Conversations Per Group (${totalOpen} open now)`} csv={{ columns: [{ key: 'name', header: 'Group' }, { key: 'open', header: 'Open Now' }], rows, filename: csvFilename('Open Conversations Per Group') }}>
              <HorizontalBars data={rows.filter((r) => r.open > 0)} bars={[{ key: 'open', name: 'Open Now' }]} />
            </ChartCard>
            <p className="u-text-muted u-text-small">
              Routed and closed are counted within the date range. Open is the number of conversations currently assigned to each group.
              Member, channel and message type filters do not apply to this tab.
            </p>
          </>
        )}
      </div>
    );
  };

  const renderReminders = () => {
    const columns = [
      { key: 'response', label: 'Response' },
      { key: 'count', label: 'Reminders' },
      { key: 'pct', label: '% of Sent', format: (r) => `${r.pct}%` },
    ];
    if (remindersFetching) return <PageLoader />;
    const title = `Reminder Responses (${reminders.total} sent, ${reminders.rate(reminders.responded)}% responded)`;
    return (
      <div className="voip-reports__charts">
        {viewMode === 'table' && (
          <ChartCard title={title} csv={{ columns: csvColumns(columns), rows: reminders.rows, filename: csvFilename('Reminder Responses') }}>
            <SortableTable columns={columns} rows={reminders.rows} defaultSort="count" rowKey={(r) => r.response} />
          </ChartCard>
        )}
        {viewMode === 'graphs' && (
          <>
            <ChartCard title={title} csv={{ columns: csvColumns(columns), rows: reminders.rows, filename: csvFilename('Reminder Responses') }}>
              <DonutChart data={reminders.byResponse} colorFor={(name, i) => REMINDER_COLORS[name] || categoryColor(i)} valueLabel="Reminders" />
            </ChartCard>
            <ChartCard title="Reminders Sent Per Day by Response" csv={{ columns: [{ key: 'date', header: 'Date' }, ...reminders.labels.map((l) => ({ key: l, header: l }))], rows: reminders.perDay, filename: csvFilename('Reminders Per Day') }}>
              <StackedBars data={reminders.perDay} xKey="date" bars={reminders.labels.map((l) => ({ key: l, name: l, color: REMINDER_COLORS[l] }))} stackId="rem" />
            </ChartCard>
            <p className="u-text-muted u-text-small">Member, channel and message type filters do not apply to this tab.</p>
          </>
        )}
      </div>
    );
  };

  const renderActiveTab = () => {
    if (activeTab === 'groups') return renderGroups();
    if (activeTab === 'reminders') return renderReminders();
    if (isLoading || isFetching) return <PageLoader />;
    if (activeTab === 'members') {
      return renderCategoryTab({
        label: 'Member', totalsRows: memberTotals, tableRows: memberTable, dailySeries: memberDaily, responseRows: memberResponse, grouping: memberGrouping, setGrouping: setMemberGrouping, nameOf: memberName,
      });
    }
    if (activeTab === 'channels') {
      return renderCategoryTab({
        label: 'Channel', totalsRows: channelTotals, tableRows: channelTable, dailySeries: channelDaily, responseRows: channelResponse, grouping: channelGrouping, setGrouping: setChannelGrouping, nameOf: channelName,
      });
    }
    return renderSummary();
  };

  return (
    <div className="app-page__container">
      <div className="app-panels">
        <div className="list-panel__wrapper">
          <div className="list-panel">
            <div className="app-page__header u-flex-direction-column">
              <div className="app-page__header__title cdr-records__title rhinoanalyst-title-row">
                Messaging Reports
                <AskReggieButton />
              </div>
            </div>
            <div className="box responsive-table cdr-records__grid">
              {renderFilters()}
              {renderTabs()}
              {activeTab === 'members' && (
                <p className="u-text-muted u-text-small">Members are credited for the messages they send. Inbound messages come from contacts, so they are not attributed to a member.</p>
              )}
              {renderActiveTab()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingReports;
